import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "dropdown", "adId", "workName", "description"]
    static values = {
        url: String,
        aircraftId: String
    }

    connect() {
        this.debounceTimer = null
        this.currentIndex = -1
        this.results = []
    }

    search(event) {
        const query = this.inputTarget.value.trim()

        // Clear previous timer
        clearTimeout(this.debounceTimer)

        // Minimum 2 characters
        if (query.length < 2) {
            this.hideDropdown()
            return
        }

        // Show loading
        this.showLoading()

        // Debounce 300ms
        this.debounceTimer = setTimeout(() => {
            this.performSearch(query)
        }, 300)
    }

    async performSearch(query) {
        try {
            const url = `${this.urlValue}?q=${encodeURIComponent(query)}&aircraft_id=${this.aircraftIdValue}`
            const response = await fetch(url, {
                headers: {
                    'Accept': 'application/json'
                }
            })

            if (!response.ok) throw new Error('Search failed')

            const ads = await response.json()
            this.results = ads

            if (ads.length > 0) {
                this.displayResults(ads)
            } else {
                this.showNoResults()
            }
        } catch (error) {
            console.error('AD search error:', error)
            this.showError()
        }
    }

    displayResults(ads) {
        this.dropdownTarget.innerHTML = ads.map((ad, index) => `
      <div class="autocomplete-item" 
           role="option"
           data-index="${index}"
           data-action="click->ad-autocomplete#select mouseenter->ad-autocomplete#highlight">
        <div class="autocomplete-ad-number">${this.escapeHtml(ad.ad_number)}</div>
        <div class="autocomplete-ad-subject">${this.escapeHtml(ad.subject)}</div>
      </div>
    `).join('')

        this.dropdownTarget.classList.remove('d-none')
        this.currentIndex = -1
    }

    showLoading() {
        this.dropdownTarget.innerHTML = '<div class="autocomplete-loading">Searching...</div>'
        this.dropdownTarget.classList.remove('d-none')
    }

    showNoResults() {
        this.dropdownTarget.innerHTML = '<div class="autocomplete-no-results">No ADs found</div>'
        this.dropdownTarget.classList.remove('d-none')
    }

    showError() {
        this.dropdownTarget.innerHTML = '<div class="autocomplete-error">Search failed. Please try again.</div>'
        this.dropdownTarget.classList.remove('d-none')
    }

    navigate(event) {
        const items = this.dropdownTarget.querySelectorAll('.autocomplete-item')
        if (items.length === 0) return

        switch(event.key) {
            case 'ArrowDown':
                event.preventDefault()
                this.currentIndex = Math.min(this.currentIndex + 1, items.length - 1)
                this.updateHighlight(items)
                break
            case 'ArrowUp':
                event.preventDefault()
                this.currentIndex = Math.max(this.currentIndex - 1, 0)
                this.updateHighlight(items)
                break
            case 'Enter':
                event.preventDefault()
                if (this.currentIndex >= 0) {
                    this.selectByIndex(this.currentIndex)
                }
                break
            case 'Escape':
                event.preventDefault()
                this.hideDropdown()
                break
        }
    }

    highlight(event) {
        const index = parseInt(event.currentTarget.dataset.index)
        this.currentIndex = index
        this.updateHighlight(this.dropdownTarget.querySelectorAll('.autocomplete-item'))
    }

    updateHighlight(items) {
        items.forEach((item, index) => {
            if (index === this.currentIndex) {
                item.classList.add('highlighted')
                item.setAttribute('aria-selected', 'true')
            } else {
                item.classList.remove('highlighted')
                item.setAttribute('aria-selected', 'false')
            }
        })
    }

    select(event) {
        const index = parseInt(event.currentTarget.dataset.index)
        this.selectByIndex(index)
    }

    selectByIndex(index) {
        if (index < 0 || index >= this.results.length) return

        const ad = this.results[index]

        // Set hidden ad_id field
        this.adIdTarget.value = ad.id

        // Set work name to AD number
        this.workNameTarget.value = ad.ad_number

        // Set description to AD subject
        this.descriptionTarget.value = ad.subject

        // Clear search input and show selected AD
        this.inputTarget.value = `${ad.ad_number} - ${ad.subject}`

        // Hide dropdown
        this.hideDropdown()

        // Focus next field
        this.workNameTarget.focus()
    }

    handleBlur(event) {
        // Small delay to allow click events to fire
        setTimeout(() => {
            this.hideDropdown()
        }, 200)
    }

    hideDropdown() {
        this.dropdownTarget.classList.add('d-none')
        this.dropdownTarget.innerHTML = ''
        this.currentIndex = -1
        this.results = []
    }

    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }
}